/**
 * Client list, the add form, and the data-quality panel.
 *
 * The merge suggestions are surfaced here rather than buried in an admin
 * screen, because the imported book contains real near-duplicates
 * ("Innoviox"/"Inoviox", "Burger Farm"/"Burgar Farm") and every per-client
 * report is wrong until they are resolved.
 *
 * Adding a client is open to any signed-in user, matching every other create
 * in the recruitment pipeline — candidates, calls, submissions, placements and
 * job postings are all the same. Gating this one would block a recruiter
 * mid-call from attaching a client that does not exist yet, and the importer
 * creates clients with no role check at all, so the gate would only ever apply
 * to the honest path. What IS gated is the destructive half: merging is
 * company-admin. Open creation, curated cleanup.
 *
 * The duplicate warning below is the answer to the risk that made a role gate
 * tempting. It catches the near-duplicate at the moment somebody types it,
 * which is the only moment anyone knows whether it is really the same client.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2, Merge, Pencil, Plus, Trash2, X } from 'lucide-react';
import DeleteRecordDialog from '../components/DeleteRecordDialog';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { formatPhone } from '../lib/phone';
import ClientEditDialog from '../components/ClientEditDialog';
import StagedList from '../components/StagedList';
import type { StagedRow } from '../components/StagedList';
import CommercialTermsFields, {
  EMPTY_TERMS,
  termsPayload,
  type CommercialTerms,
} from '../components/CommercialTermsFields';
import { isCompanyAdmin, isSupervisor, useAuth } from '../store/auth';
import type { Client, ClientLifecycle, Page } from '../types';
import { titleCaseName } from '../lib/titleCase';

interface MergeSuggestion {
  keep: { id: string; name: string; usage: number };
  merge: { id: string; name: string; usage: number };
  similarity: number;
}

interface Suggestions {
  mergeSuggestions: MergeSuggestion[];
  notAClient: { id: string; name: string; reason: string }[];
}

/**
 * The same collapse the alias table uses server-side.
 *
 * ``parsers.normalise_key`` strips everything but letters and digits, which is
 * what makes "Tata 1MG", "Tata-1mg" and "Tata 1 MG" one client rather than
 * three. Matching the rule here means the warning fires on exactly the
 * spellings the importer would later have to merge.
 */
function normaliseKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * Clients whose names would collapse to the same key, or nearly.
 *
 * Exact collapse first — those are certain. Then a containment check, which
 * catches "Tata" against "Tata 1MG": not the same key, but the pair a person
 * should look at before creating the second one.
 */
/** One interview point, held before the client it belongs to exists. */
type StagedContact = {
  name: string;
  designation: string | null;
  phone: string | null;
  email: string | null;
  location: string | null;
};

/**
 * The row of inputs that adds one interview point.
 *
 * Its own component with its own state, so adding somebody clears these five
 * boxes without touching the client form around them — and so pressing Enter
 * here adds a contact rather than submitting the whole dialog, which is what a
 * nested form would otherwise do.
 */
function StagedContactFields({ onAdd }: { onAdd: (c: StagedContact) => void }) {
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');

  function add() {
    // Only the name is required, matching the API: a name with nothing beside
    // it is still worth recording, and refusing it is how half a panel goes
    // unrecorded.
    if (!name.trim()) return;
    onAdd({
      name: name.trim(),
      designation: designation.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      location: location.trim() || null,
    });
    setName('');
    setDesignation('');
    setPhone('');
    setEmail('');
    setLocation('');
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600">
          Name
          <input
            spellCheck={false}
            className="input mt-1"
            value={name}
            maxLength={160}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          placeholder="Priya Menon"
            />
        </label>
        <label className="block text-xs text-gray-600">
          Designation
          <input
            spellCheck={false}
            className="input mt-1"
            value={designation}
            maxLength={160}
            onChange={(e) => setDesignation(e.target.value)}
          placeholder="HR Manager"
            />
        </label>
        <label className="block text-xs text-gray-600">
          Mobile
          <input
            spellCheck={false}
            className="input mt-1"
            inputMode="numeric"
            placeholder="10 digits"
            value={phone}
            maxLength={20}
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <label className="block text-xs text-gray-600">
          Email
          <input
            spellCheck={false}
            type="email"
            className="input mt-1"
            value={email}
            maxLength={255}
            onChange={(e) => setEmail(e.target.value)}
          placeholder="priya@example.com"
            />
        </label>
        <label className="col-span-2 block text-xs text-gray-600">
          Location
          <input
            spellCheck={false}
            className="input mt-1"
            value={location}
            maxLength={160}
            onChange={(e) => setLocation(e.target.value)}
          placeholder="Gurugram"
            />
        </label>
      </div>
      <button
        type="button"
        className="btn-ghost mt-2 text-xs"
        onClick={add}
        disabled={!name.trim()}
      >
        <Plus className="h-3.5 w-3.5" />
        Add interview point
      </button>
    </div>
  );
}

function looksLikeExisting(name: string, clients: Client[]): Client[] {
  const key = normaliseKey(name);
  if (key.length < 3) return [];
  return clients.filter((c) => {
    const existing = normaliseKey(c.name);
    return existing === key || existing.includes(key) || key.includes(existing);
  });
}

function AddClientDialog({
  existing,
  saving,
  onCancel,
  onSave,
}: {
  existing: Client[];
  saving: boolean;
  onCancel: () => void;
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  /**
   * On the form because of what it decides, not because the row has room.
   *
   * The client's state is half of the comparison that splits an invoice into
   * CGST and SGST or leaves it as one IGST line. Absent, the document shows a
   * single unnamed GST line and records a warning — so a client added without
   * it produces wrong-looking invoices until somebody thinks to edit them.
   */
  const [state, setState] = useState('');
  const [industry, setIndustry] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [terms, setTerms] = useState<CommercialTerms>(EMPTY_TERMS);
  // Blank by default and never prefilled. A rate this product suggested is a
  // rate nobody checked, and these end up on invoices sent to real clients.
  const [gstRate, setGstRate] = useState('');
  const [tdsRate, setTdsRate] = useState('');
  const [tdsSection, setTdsSection] = useState('');
  const [website, setWebsite] = useState('');
  // Three lists, each written after the client has an id -- the same staging
  // the interview points already use, for the same reason: none of them can
  // hang off a client that does not exist yet.
  const [escalations, setEscalations] = useState<StagedRow[]>([]);
  const [addresses, setAddresses] = useState<StagedRow[]>([]);
  const [registrations, setRegistrations] = useState<StagedRow[]>([]);
  /**
   * The panel, staged until the client exists.
   *
   * Interview contacts hang off a client id, and there is no client id until
   * the POST returns — so they are collected here and written afterwards. The
   * alternative is making somebody create the client, find it again and then
   * add the people they already had in front of them.
   */
  const [contacts, setContacts] = useState<StagedContact[]>([]);

  const similar = useMemo(() => looksLikeExisting(name, existing), [name, existing]);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      {/* Sized for what it holds now. It was max-w-md with everything in one
          column and no height cap — fine for four fields, and it has eleven
          since the tax block and the escalation point were added: on a laptop
          "Add client" sat below the fold with nothing to scroll. Header and
          footer are fixed, only the middle scrolls. */}
      <form
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) {
            toast.error('A client needs a name');
            return;
          }
          onSave({
            name: name.trim(),
            address: address.trim() || null,
            city: city.trim() || null,
            state: state.trim() || null,
            industry: industry.trim() || null,
            contactName: contactName.trim() || null,
            contactEmail: contactEmail.trim() || null,
            contactPhone: contactPhone.trim() || null,
            // Fee basis, fee percent or flat amount, payment tenure and
            // replacement period, all from the shared control set so this
            // form and the edit dialog cannot disagree about them.
            ...termsPayload(terms),
            // Null, not zero, when left blank. The server treats null as "not
            // established" and applies nothing; zero would read as a decision
            // somebody made.
            gstRate: gstRate.trim() || null,
            tdsRate: tdsRate.trim() || null,
            tdsSection: tdsSection.trim() || null,
            website: website.trim() || null,
            escalationContacts: escalations,
            addresses,
            gstRegistrations: registrations,
            // Not part of the client row. Pulled back out by the caller and
            // written once the client has an id — see the create mutation.
            interviewContacts: contacts,
          });
        }}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">Add a Client</h3>
          <button type="button" className="btn-ghost" onClick={onCancel} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
        <label className="block text-xs text-gray-600">
          Client Name
          <input
            spellCheck={false}
            className="input mt-1"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={160}
            placeholder="Tata 1MG"
          />
        </label>

        {/* Shown, never enforced. Two branches of one company are a real
            thing, and only the person typing knows which case this is. */}
        {similar.length > 0 && (
          <div className="rounded bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            <p className="font-medium">
              {similar.length === 1 ? 'This looks like' : 'This looks like'}{' '}
              {similar.map((c) => c.name).join(', ')}.
            </p>
            <p className="mt-0.5">
              Adding it again would split every report for that client in two.
              Carry on if it really is a different one.
            </p>
          </div>
        )}

        {/* The head office proper, which until now had nowhere to go: this
            label sat on the 100-character `city` column, so it asked for an
            address and stored a city. The address is its own field now and
            City is a city again. */}
        <label className="block text-xs text-gray-600">
          Head Office Address
          <textarea
            spellCheck={false}
            className="input mt-1"
            rows={2}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="As on the GST certificate — building, street, area, landmark"
          />
          <span className="mt-0.5 block text-[11px] text-gray-400">
            The one that goes on their invoices. Other locations go in Job
            Locations below.
          </span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-gray-600">
            City
            <input
              spellCheck={false}
              className="input mt-1"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={100}
              placeholder="Gurugram"
            />
          </label>

          <label className="text-xs text-gray-600">
            State
            <input
              spellCheck={false}
              className="input mt-1"
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxLength={100}
            placeholder="Haryana"
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Decides whether their invoices carry IGST or CGST + SGST.
            </span>
          </label>

          <label className="text-xs text-gray-600">
            Industry
            <input
              spellCheck={false}
              className="input mt-1"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              maxLength={120}
            placeholder="Healthcare"
            />
          </label>

          <label className="text-xs text-gray-600">
            Client Contact Name
            <input
              spellCheck={false}
              className="input mt-1"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              maxLength={160}
            placeholder="Priya Menon"
            />
          </label>

          <label className="text-xs text-gray-600">
            Contact Mobile
            <input
              spellCheck={false}
              className="input mt-1"
              inputMode="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              maxLength={20}
            placeholder="9876543210"
            />
          </label>

          <label className="text-xs text-gray-600">
            Contact Email
            <input
              spellCheck={false}
              className="input mt-1"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              maxLength={255}
            placeholder="priya@example.com"
            />
          </label>
        </div>

        {/* Agreed once, applied per placement. */}
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="mb-2 text-xs font-medium text-gray-700">
            Commercial Terms
          </p>
          <CommercialTermsFields value={terms} onChange={setTerms} />
        </div>

        <label className="block text-xs text-gray-600">
          Website
          <input
            spellCheck={false}
            className="input mt-1"
            type="url"
            maxLength={255}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://example.com"
          />
        </label>

        {/* A list now, not three boxes. Escalation goes to a state-wise HR
            head rather than to one office, which is a set. */}
        <StagedList
          legend="Escalation Points"
          addLabel="Add escalation point"
          hint="Who to go to when the day-to-day contact is not answering."
          rows={escalations}
          onChange={setEscalations}
          summary={(r) =>
            [r.name, r.designation, formatPhone(r.phone), r.location]
              .filter(Boolean)
              .join(' \u00b7 ')
          }
          fields={[
            { key: 'name', label: 'Name', required: true, maxLength: 160,
              placeholder: 'Priya Menon' },
            { key: 'designation', label: 'Position', maxLength: 160,
              placeholder: 'HR Head' },
            { key: 'phone', label: 'Mobile', inputMode: 'numeric', maxLength: 20,
              placeholder: '10 digits' },
            { key: 'email', label: 'Email', type: 'email', maxLength: 255,
              placeholder: 'priya@example.com' },
            { key: 'location', label: 'Location', maxLength: 160,
              placeholder: 'Gurugram' },
          ]}
        />

        {/* The other places this client operates. The head office above is a
            single field because there is one of it and it goes on the
            invoice; these are where the work happens and where the HR sits. */}
        <StagedList
          legend="Job Locations / Client Addresses"
          addLabel="Add address"
          hint="Where the work happens. The head office is the field above."
          rows={addresses}
          onChange={setAddresses}
          summary={(r) =>
            [r.label, r.line, r.city, r.state, r.pincode]
              .filter(Boolean)
              .join(' \u00b7 ')
          }
          fields={[
            { key: 'label', label: 'Name for this place', maxLength: 120,
              placeholder: 'Gurugram office' },
            { key: 'line', label: 'Address', required: true, wide: true,
              maxLength: 255, placeholder: 'Plot 12, Udyog Vihar Phase IV' },
            { key: 'city', label: 'City', maxLength: 100,
              placeholder: 'Gurugram' },
            { key: 'state', label: 'State', maxLength: 100,
              placeholder: 'Haryana' },
            { key: 'pincode', label: 'PIN code', inputMode: 'numeric',
              maxLength: 10, placeholder: '122015' },
          ]}
        />

        {/* One registration per state, which is what the law gives them.
            Which one an invoice bills decides IGST against CGST+SGST, so the
            state is required here -- a registration that cannot say where it
            is cannot answer the question it exists for. */}
        <StagedList
          legend="GST Registrations"
          addLabel="Add registration"
          hint="One per state. The invoice asks which one to bill; it never guesses."
          rows={registrations}
          onChange={setRegistrations}
          summary={(r) =>
            [r.gstin, r.state, r.label].filter(Boolean).join(' \u00b7 ')
          }
          fields={[
            { key: 'gstin', label: 'GSTIN', required: true, maxLength: 15,
              placeholder: '06AAFCD7691C2ZM' },
            { key: 'state', label: 'State', maxLength: 100,
              placeholder: 'Haryana' },
            { key: 'label', label: 'Name for this one', maxLength: 120,
              placeholder: 'North branch' },
            { key: 'address', label: 'Registered address', wide: true,
              placeholder: 'As filed against this GSTIN' },
          ]}
        />

        {/* The people who interview, collected here so a client arrives
            complete. A panel is a list, unlike the single escalation contact
            above it, and it is written after the client exists — see the
            staging note on the state. */}
        <fieldset className="rounded-lg border border-gray-200 p-3">
          <legend className="px-1 text-xs font-medium text-gray-600">
            Interview Points
          </legend>

          {contacts.length > 0 && (
            <ul className="mb-2 space-y-1">
              {contacts.map((c, index) => (
                <li
                  key={`${c.name}-${index}`}
                  className="flex items-center justify-between gap-2 rounded bg-gray-50 px-2 py-1 text-xs"
                >
                  <span className="min-w-0 truncate text-gray-700">
                    {[c.name, c.designation, formatPhone(c.phone), c.location]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 text-gray-400 hover:text-red-600"
                    aria-label={`Remove ${c.name}`}
                    onClick={() =>
                      setContacts(contacts.filter((_, i) => i !== index))
                    }
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <StagedContactFields
            onAdd={(contact) => setContacts([...contacts, contact])}
          />
        </fieldset>

        {/* Optional, and deliberately unprefilled. What this client is
            charged and what they deduct is a determination somebody makes with
            their accountant — leaving these empty means no tax figure is shown
            on their invoices at all, which is the honest default. */}
        <fieldset className="rounded-lg border border-gray-200 p-3">
          <legend className="px-1 text-xs font-medium text-gray-600">
            Tax Treatment
          </legend>

          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs text-gray-600">
              GST %
              <input
                spellCheck={false}
                className="input mt-1"
                inputMode="decimal"
                value={gstRate}
                onChange={(e) => setGstRate(e.target.value)}
                placeholder="18"
              />
            </label>
            <label className="block text-xs text-gray-600">
              TDS %
              <input
                spellCheck={false}
                className="input mt-1"
                inputMode="decimal"
                value={tdsRate}
                onChange={(e) => setTdsRate(e.target.value)}
                placeholder="10"
              />
            </label>
          </div>

          <label className="mt-2 block text-xs text-gray-600">
            TDS Section
            <input
              spellCheck={false}
              className="input mt-1"
              value={tdsSection}
              onChange={(e) => setTdsSection(e.target.value)}
              maxLength={20}
              placeholder="194J"
            />
          </label>

          {/* A single flat GSTIN used to sit here, and it was asking the
              same question as the GST Registrations editor above -- which the
              client's own specification asks for location-wise, with a plus
              button, because one entity holds one registration per state.
              Two boxes for one fact is how they come to disagree, and the
              one on this form was the one that could not say which state it
              was for.

              The column behind it still exists and is still read: it is the
              fallback an invoice uses when no registration was chosen, which
              is every invoice raised before registrations existed. It is
              editable on the edit dialog, so the value on the clients that
              have one stays correctable. */}
        </fieldset>
        </div>

        {/* Outside the scrolling area, so "Add client" never drifts off the
            bottom however many fields the form grows. */}
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Add client
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Whether a client has been vetted.
 *
 * One of the two axes, and only shown when there is something to say.
 * "Approved" is the ordinary state and a badge on every row saying so is
 * noise -- what a reader is scanning for is the row that still needs a
 * decision, or the one that was turned down.
 */
function ApprovalBadge({ client: row }: { client: Client }) {
  if (row.approvalStatus === 'pending') {
    return (
      <span
        className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800"
        title="Can be called, tracked to and sourced against. Cannot be invoiced until approved."
      >
        Awaiting approval
      </span>
    );
  }
  if (row.approvalStatus === 'rejected') {
    return (
      <span
        className="rounded bg-red-100 px-1.5 py-0.5 text-[11px] text-red-700"
        title={row.approvalDecisionNote ?? undefined}
      >
        Rejected
      </span>
    );
  }
  return <span className="text-[11px] text-gray-300">—</span>;
}

/** What each lifecycle state means, on the control that sets it. */
const LIFECYCLE_LABELS: Record<ClientLifecycle, string> = {
  pipeline: 'Pipeline',
  active: 'Active',
  inactive: 'Inactive',
};

const LIFECYCLE_TONE: Record<ClientLifecycle, string> = {
  pipeline: 'bg-sky-100 text-sky-800',
  active: 'bg-emerald-50 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
};

/**
 * Where the relationship stands, and the control that changes it.
 *
 * Its own column rather than sharing one with approval. They are independent
 * axes -- an approved client can be any of the three -- and collapsing them
 * into a single badge meant a client awaiting approval showed no lifecycle at
 * all, so "which of these are prospects" could not be answered by looking.
 *
 * Inline rather than only inside the edit dialog. The dialog has the same
 * control and keeps it, but triaging a list of clients into prospects and
 * dormant ones is a column of small decisions, and sending somebody through a
 * twenty-field modal for each is how it does not get done.
 *
 * A plain badge for anybody who may not edit: the server refuses the PATCH
 * below company admin regardless, so the select would be a control that
 * always fails.
 */
function LifecycleCell({
  client: row,
  mayEdit,
  saving,
  onChange,
}: {
  client: Client;
  mayEdit: boolean;
  saving: boolean;
  onChange: (next: ClientLifecycle) => void;
}) {
  if (!mayEdit) {
    return (
      <span
        className={`rounded px-1.5 py-0.5 text-[11px] ${LIFECYCLE_TONE[row.lifecycle]}`}
      >
        {LIFECYCLE_LABELS[row.lifecycle]}
      </span>
    );
  }
  return (
    <select
      className="input py-1 text-xs"
      value={row.lifecycle}
      disabled={saving}
      aria-label={`Relationship with ${row.name}`}
      onChange={(e) => onChange(e.target.value as ClientLifecycle)}
    >
      <option value="pipeline">Pipeline</option>
      <option value="active">Active</option>
      <option value="inactive">Inactive</option>
    </select>
  );
}


export default function ClientsPage() {
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  /**
   * Which client the edit dialog is showing.
   *
   * Company admin and above. The server refuses PATCH /clients/{id} below that
   * regardless, so hiding the button is courtesy and the 403 is the rule.
   */
  const [editing, setEditing] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  /**
   * Whether to narrow the page to the clients somebody typed in here.
   *
   * Off by default, so the page shows the whole book. It used to default the
   * other way, hiding every imported client, and that was sized against a
   * development database of 998 clients where 997 came out of a tracker sheet
   * -- there, the handful anybody had added really was lost in the list.
   * Production is not that shape: 106 clients after the duplicates were
   * merged, of which two were typed in. Hiding 104 of 106 rows to surface two
   * does not rescue the page, it empties it.
   *
   * Kept as a toggle rather than deleted, because "just the ones we added" is
   * still a question worth asking -- it is simply not the answer to what the
   * page should open on.
   */
  const [manualOnly, setManualOnly] = useState(false);
  const mayEdit = isCompanyAdmin(user);

  const { data: clients, isLoading } = useQuery<Page<Client>>({
    queryKey: ['clients', manualOnly],
    queryFn: async () =>
      (
        await client.get('/api/v1/clients', {
          params: {
            limit: 200,
            ...(manualOnly ? { source: 'manual' } : {}),
          },
        })
      ).data,
  });

  /**
   * Near-duplicate clients, which only mean anything within one company.
   *
   * Gated on an acting company as well as on the role. The route resolves a
   * company through resolve_company_id, which refuses an owner who has not
   * chosen one -- correctly, since "near-duplicates across every tenant" is
   * not a question with an answer. Without the gate an owner sitting on
   * "Choose a company…" fired it anyway and collected a 400 in the console for
   * a panel that was never going to render.
   *
   * The key carries the company, so switching companies fetches that
   * company's duplicates rather than showing the previous one's.
   */
  const actingCompanyId = useAuth((s) => s.actingCompanyId);
  const forCompany = actingCompanyId ?? user?.companyId ?? null;

  const { data: suggestions } = useQuery<Suggestions>({
    queryKey: ['client-merge-suggestions', forCompany],
    queryFn: async () =>
      (await client.get('/api/v1/clients/merge-suggestions')).data,
    /*
     * And gated on the same toggle as the list below it, which now means it
     * shows by default.
     *
     * Every pair this panel can find is a pair of imported spellings --
     * "CtrlAlt Heal" and "CrlAltHeal", one keystroke apart. While the list
     * hid those rows, reporting thirty duplicate clients over a list of one
     * was the page arguing with itself, so the panel followed the toggle.
     * That reasoning holds in the new direction: the rows it talks about are
     * on the page now, and merging is how a duplicate stops being one.
     *
     * It goes away when somebody narrows to what they typed in, because then
     * the pairs it names are not on screen again.
     */
    enabled: isSupervisor(user) && Boolean(forCompany) && !manualOnly,
  });

  const create = useMutation({
    // No company_id here: the request interceptor puts the acting company on
    // every call, and the server resolves it for everybody else.
    mutationFn: async (payload: Record<string, unknown>) => {
      // The panel is not part of the client row. It is pulled out here and
      // written once the client has an id, because that is the first moment
      // the contacts have anything to hang off.
      const {
        interviewContacts = [],
        escalationContacts = [],
        addresses = [],
        gstRegistrations = [],
        ...clientFields
      } = payload as {
        interviewContacts?: StagedContact[];
        escalationContacts?: StagedRow[];
        addresses?: StagedRow[];
        gstRegistrations?: StagedRow[];
      } & Record<string, unknown>;

      const created = (await client.post('/api/v1/clients', clientFields))
        .data as Client;

      // Sequential throughout, so a failure names the row it failed on rather
      // than arriving as one of a dozen simultaneous errors.
      for (const [path, rows] of [
        ['interview-contacts', interviewContacts],
        ['escalation-contacts', escalationContacts],
        ['addresses', addresses],
        ['gst-registrations', gstRegistrations],
      ] as const) {
        for (const row of rows) {
          await client.post(`/api/v1/clients/${created.id}/${path}`, row);
        }
      }
      return created;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // The new name may itself be a near-duplicate somebody accepted, so the
      // suggestions have to be recomputed rather than left stale.
      queryClient.invalidateQueries({ queryKey: ['client-merge-suggestions'] });
      setAdding(false);
      toast.success(`${created.name} added`);
      // Straight to their page with the requirement form open. A client is
      // added because somebody is about to raise a requirement for them, and
      // sending them back to a list to find the client they just typed is how
      // the requirement goes unlogged — which is what leaves a tracked
      // candidate with nothing to sit under.
      navigate(`/clients/${created.id}`, { state: { raiseRequirement: true } });
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not add that client')),
  });

  /**
   * Approving or rejecting a client.
   *
   * The half of the approval workflow that was missing: the states and the
   * routes existed, the list drew a badge, and there was no way to decide one
   * from the screen. Company admin and above, matching the server.
   *
   * Approval gates money and nothing else. A pending client can be rung,
   * tracked to and sourced against; what it cannot be is invoiced. So the
   * prompt on rejection asks for a reason rather than a confirmation -- the
   * reason is what somebody reads to know what to correct.
   */
  const decide = useMutation({
    mutationFn: async ({ id, approve, note }: { id: string; approve: boolean; note?: string }) =>
      (
        await client.post(
          `/api/v1/clients/${id}/${approve ? 'approve' : 'reject'}`,
          approve ? undefined : { note },
        )
      ).data as Client,
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      // The billing picker offers approved clients only, so a decision here
      // changes what the invoice form can see.
      void queryClient.invalidateQueries({ queryKey: ['client-options'] });
      toast.success(
        saved.approvalStatus === 'approved'
          ? `${saved.name} approved`
          : `${saved.name} rejected`,
      );
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not record that decision')),
  });

  /**
   * Moving a client between prospect, active and dormant.
   *
   * Its own mutation rather than reusing `update`, which closes the edit
   * dialog and toasts "updated" on success -- neither of which makes sense
   * for a select changed in place. Company admin, matching the server.
   */
  const setLifecycle = useMutation({
    mutationFn: async ({ id, lifecycle }: { id: string; lifecycle: ClientLifecycle }) =>
      (await client.patch(`/api/v1/clients/${id}`, { lifecycle })).data as Client,
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      // The two pickers offer different lifecycle states, so moving a client
      // changes what each of them can see.
      void queryClient.invalidateQueries({ queryKey: ['client-options'] });
      toast.success(`${saved.name} is now ${LIFECYCLE_LABELS[saved.lifecycle]}`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not change that')),
  });

  const update = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      (await client.patch(`/api/v1/clients/${id}`, payload)).data as Client,
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // The invoice worklist reads these rates to show what is on file, so a
      // rate set here has to reach that screen without a reload.
      queryClient.invalidateQueries({ queryKey: ['uninvoiced-placements'] });
      setEditing(null);
      toast.success(`${saved.name} updated`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not save that client')),
  });

  const merge = useMutation({
    mutationFn: async (payload: { keepId: string; mergeId: string }) =>
      (await client.post('/api/v1/clients/merge', payload)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-merge-suggestions'] });
      toast.success(data.detail);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-6">
      {suggestions && suggestions.mergeSuggestions.length > 0 && (
        <section className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            Possible duplicate clients ({suggestions.mergeSuggestions.length})
          </h2>
          <p className="mt-1 text-sm text-amber-800">
            These names are similar enough to be the same company. Reports stay
            split until they are merged — but check each one, since similar names
            can still be different businesses.
          </p>
          <ul className="mt-3 space-y-2">
            {suggestions.mergeSuggestions.map((s) => (
              <li
                key={`${s.keep.id}-${s.merge.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2"
              >
                <span className="text-sm">
                  Keep <strong>{s.keep.name}</strong>{' '}
                  <span className="text-gray-400">({s.keep.usage} records)</span>
                  {' · merge '}
                  <strong>{s.merge.name}</strong>{' '}
                  <span className="text-gray-400">({s.merge.usage})</span>
                </span>
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  disabled={merge.isPending}
                  onClick={() =>
                    merge.mutate({ keepId: s.keep.id, mergeId: s.merge.id })
                  }
                >
                  <Merge className="h-3.5 w-3.5" />
                  Merge
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {suggestions && suggestions.notAClient.length > 0 && (
        <section className="rounded-xl bg-gray-50 p-4 ring-1 ring-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">
            Probably Not Client Names
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            These look like job titles or statuses typed into the client column
            during import: {suggestions.notAClient.map((c) => c.name).join(', ')}.
          </p>
        </section>
      )}

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">
            Clients {clients && <span className="badge bg-gray-100 text-gray-600">{clients.total}</span>}
          </h2>
          <div className="flex items-center gap-3">
            {/* Off by default, so the page opens on the whole book. The
                reverse default was sized against a development database where
                997 of 998 clients came out of a tracker sheet; production has
                106, so hiding the imported ones left two rows on screen and
                read as a page that had lost everything.

                This also governs the near-duplicate panel above, which talks
                about imported spellings: it belongs with the rows it names. */}
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={manualOnly}
                onChange={(e) => setManualOnly(e.target.checked)}
              />
              Only clients added here
            </label>
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={() => setAdding(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Client
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Name</th>
                  <th className="px-4 py-2.5 font-medium">Approval</th>
                  <th className="px-4 py-2.5 font-medium">Relationship</th>
                  <th className="px-4 py-2.5 font-medium">City</th>
                  <th className="px-4 py-2.5 font-medium">Contact</th>
                  <th className="px-4 py-2.5 font-medium">Terms</th>
                  <th className="px-4 py-2.5 font-medium">Tax</th>
                  {mayEdit && <th className="px-4 py-2.5 font-medium"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients?.items.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium">
                      {/* The name is the way in. Everything about a client —
                          their people, their panel, their requirements — is on
                          one page now, and the list is how you reach it. */}
                      <Link
                        to={`/clients/${c.id}`}
                        className="text-indigo-600 hover:underline"
                      >
                        {c.name}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <ApprovalBadge client={c} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <LifecycleCell
                        client={c}
                        mayEdit={mayEdit}
                        saving={setLifecycle.isPending}
                        onChange={(next) =>
                          setLifecycle.mutate({ id: c.id, lifecycle: next })
                        }
                      />
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{c.city ?? '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {titleCaseName(c.contactName) ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {c.paymentTermsDays} days
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">
                      {/* Amber where nothing is set, because that is why an
                          invoice for them carries no tax line — a blank cell
                          would read as "nothing to see". */}
                      {c.gstRate || c.tdsRate ? (
                        <>
                          {c.gstRate && <span>GST {c.gstRate}%</span>}
                          {c.gstRate && c.tdsRate && <span> · </span>}
                          {c.tdsRate && <span>TDS {c.tdsRate}%</span>}
                        </>
                      ) : (
                        <span className="text-amber-700">None set</span>
                      )}
                    </td>
                    {mayEdit && (
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => setEditing(c)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        {/* Only where there is a decision to make. An approved
                            or rejected client has one already, and re-deciding
                            it would overwrite the name of whoever decided
                            first — which is the question the column answers. */}
                        {c.approvalStatus === 'pending' && (
                          <>
                            <button
                              type="button"
                              className="btn-ghost ml-1 text-xs text-emerald-700"
                              disabled={decide.isPending}
                              onClick={() => decide.mutate({ id: c.id, approve: true })}
                              title="Approve this client, which is what lets it be invoiced"
                            >
                              <Check className="h-3.5 w-3.5" />
                              Approve
                            </button>
                            <button
                              type="button"
                              className="btn-ghost ml-1 text-xs text-amber-700"
                              disabled={decide.isPending}
                              onClick={() => {
                                // The reason is the point of a rejection: it is
                                // what somebody reads to know what to correct
                                // before asking again. The server requires it.
                                const note = window.prompt(
                                  `Why is ${c.name} being rejected? This is kept with the client.`,
                                );
                                if (note && note.trim()) {
                                  decide.mutate({
                                    id: c.id,
                                    approve: false,
                                    note: note.trim(),
                                  });
                                }
                              }}
                              title="Send this client back with a reason"
                            >
                              <X className="h-3.5 w-3.5" />
                              Reject
                            </button>
                          </>
                        )}
                        {/* Deleting a client takes its pipeline and its
                            billing history with it. Setting the client
                            inactive on the Edit dialog is the reversible
                            answer; this is not. */}
                        <button
                          type="button"
                          className="btn-ghost ml-1 text-xs text-red-600"
                          onClick={() => setDeletingClient(c)}
                          title="Delete this client and everything filed against it"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {deletingClient && (
        <DeleteRecordDialog
          key={deletingClient.id}
          noun="client"
          checkUrl={`/api/v1/clients/${deletingClient.id}/delete-check`}
          deleteUrl={`/api/v1/clients/${deletingClient.id}`}
          /* Submissions, placements, invoices and requirements all go with
             it. Calls survive with an empty client column, which is the only
             part of this that can be put back by hand. */
          cascade
          confirm={{
            phrase: deletingClient.name,
            field: 'confirmName',
            prompt: `Type ${deletingClient.name} to confirm`,
          }}
          onClose={() => setDeletingClient(null)}
          onDeleted={() => {
            setDeletingClient(null);
            void queryClient.invalidateQueries({ queryKey: ['clients'] });
            void queryClient.invalidateQueries({ queryKey: ['client-options'] });
            void queryClient.invalidateQueries({
              queryKey: ['client-merge-suggestions'],
            });
          }}
        />
      )}

      {editing && (
        <ClientEditDialog
          key={editing.id}
          client={editing}
          saving={update.isPending}
          onSave={(payload) => update.mutate({ id: editing.id, payload })}
          onCancel={() => setEditing(null)}
        />
      )}

      {adding && (
        <AddClientDialog
          existing={clients?.items ?? []}
          saving={create.isPending}
          onCancel={() => setAdding(false)}
          onSave={(payload) => create.mutate(payload)}
        />
      )}
    </div>
  );
}
